import { useMemo } from 'react';

import { mergeMenuConfig } from '@/lib/menu/menuConfig';
import { useMenuSettings } from '@/lib/menu/useMenuSettings';
import { useIsMobile } from '@/lib/utils/useIsMobile';

/**
 * 화면 제목에 쓸 메뉴 라벨.
 *
 * 관리자가 메뉴 관리에서 이름을 바꾸면 사이드바와 화면 제목이 함께 바뀌어야 한다.
 * v1 `lib/useMenuLabel.ts` 와 같은 역할이며, 설정을 못 받아온 동안에는 fallback 이 뜬다.
 */
export function useMenuLabel(menuKey: string, fallback: string): string {
  const { data: menuSettings } = useMenuSettings();

  return useMemo(
    () => mergeMenuConfig(menuSettings).find((item) => item.menuKey === menuKey)?.label ?? fallback,
    [fallback, menuKey, menuSettings],
  );
}

/**
 * 그룹과 하위 항목을 함께 보여줘야 하는 화면용. 예: `업무 일정 · 주간업무 · 준공현장`
 *
 * <h2>좁은 화면에서는 그룹 이름만</h2>
 * 세 마디를 이으면 모바일 제목 칸에 들어가지 않아 「업무 일정 · 주…」 로 잘렸다 — 잘린 제목은
 * 아무 말도 하지 않는다(사용자 지적 2026-08-31). 그래서 <b>맨 앞 한 마디만</b> 남긴다. 뒤 두 마디는
 * 바로 아래 탭 줄이 이미 말하고 있고(본사·현장·준공현장), 어느 화면에서 왔는지는 방금 누른
 * 메뉴가 말한다.
 *
 * @param scopeLabel 화면 안의 갈래(`준공현장`·`본사`…). 넓은 화면에서만 붙는다.
 */
export function useCombinedMenuLabel(
  parentKey: string,
  childKey: string,
  fallback: string,
  scopeLabel?: string,
): string {
  const { data: menuSettings } = useMenuSettings();
  const isMobile = useIsMobile();

  return useMemo(() => {
    const merged = mergeMenuConfig(menuSettings);
    const parentLabel = merged.find((item) => item.menuKey === parentKey)?.label;
    const childLabel = merged.find((item) => item.menuKey === childKey)?.label;

    if (isMobile) return parentLabel ?? childLabel ?? fallback;

    const base = parentLabel && childLabel ? `${parentLabel} · ${childLabel}` : childLabel ?? fallback;
    return scopeLabel ? `${base} · ${scopeLabel}` : base;
  }, [childKey, fallback, isMobile, menuSettings, parentKey, scopeLabel]);
}
